import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './afonsodanielsouza.module.scss';
import InstagramIcon from '../../icons/instagramIcon.tsx';
import WhatsappIcon from '../../icons/whatsappIcon.tsx';

const WHATSAPP_NUMBER = '5517981972950';
const WHATSAPP_MSG = 'Olá! Vim pelo cartão digital e gostaria de saber mais...';
const INSTAGRAM_URL = 'https://www.instagram.com/afonsodanielsouza?igsh=MXgzMXBieDFkM3Q3cQ==';
const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`;

/** Tempo mínimo na tela: alinhado à animação da barra (~2,1s) + logo */
const LOADER_MIN_MS = 2400;

const loaderAsset = require('./src/loader.png');
const backgroundAsset = require('./src/background-links.png');
const bannerAsset = require('./src/banner-1.png');
const bannerAsset2 = require('./src/banner-2.png');
const bannerAsset3 = require('./src/banner-3.png');

function preloadImage(src: string): Promise<void> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });
}

type AfonsodanielsouzaLocationState = { skipLoader?: boolean };

const Afonsodanielsouza: React.FC = () => {
    const location = useLocation();
    const skipLoader =
        (location.state as AfonsodanielsouzaLocationState | null)?.skipLoader === true;

    const [showLoader, setShowLoader] = useState(() => !skipLoader);
    const [loaderExiting, setLoaderExiting] = useState(false);

    useEffect(() => {
        if (!showLoader) return;
        const html = document.documentElement;
        const body = document.body;
        const prevHtmlOverflow = html.style.overflow;
        const prevBodyOverflow = body.style.overflow;
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        return () => {
            html.style.overflow = prevHtmlOverflow;
            body.style.overflow = prevBodyOverflow;
        };
    }, [showLoader]);

    useEffect(() => {
        if (skipLoader) return;

        let cancelled = false;
        const run = async () => {
            const t0 = performance.now();
            await Promise.all([
                preloadImage(loaderAsset),
                preloadImage(backgroundAsset),
                preloadImage(bannerAsset),
                document.fonts.ready.catch(() => undefined),
            ]);
            const elapsed = performance.now() - t0;
            await new Promise((r) => setTimeout(r, Math.max(0, LOADER_MIN_MS - elapsed)));
            if (cancelled) return;
            setLoaderExiting(true);
            window.setTimeout(() => {
                if (!cancelled) setShowLoader(false);
            }, 560);
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [skipLoader]);

    return (
        <div className={styles.afonsodanielsouza} aria-busy={showLoader}>
            {showLoader && (
                <div
                    className={`${styles.loaderOverlay} ${loaderExiting ? styles.loaderOverlayExiting : ''}`}
                    role="status"
                    aria-live="polite"
                    aria-label="Carregando"
                    aria-hidden={loaderExiting}
                >
                    <div className={styles.loaderInner}>
                        <div className={styles.loaderReveal}>
                            <img
                                src={loaderAsset}
                                alt=""
                                className={styles.loaderImage}
                                decoding="async"
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.page}>
                <div className={styles.hero}>
                    <img
                        src={backgroundAsset}
                        alt="Daniel Afonso - Personal trainer"
                        className={styles.heroImage}
                    />
                </div>

                <div className={styles.content}>
                    <div className={styles.statsRow}>
                        <div className={styles.statBlock}>
                            <span className={styles.statValue}>+1000</span>
                            <span className={styles.statLabel}>Alunos</span>
                        </div>
                        <div className={styles.statBlock}>
                            <span className={styles.statValue}>+19</span>
                            <span className={styles.statLabel}>Anos de experiência</span>
                        </div>
                    </div>
                    <div className={styles.socialRow}>
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.socialLink}
                            aria-label="Instagram"
                        >
                            <InstagramIcon height="26" color="#D7282F" />
                        </a>
                        <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.socialLink}
                            aria-label="WhatsApp"
                        >
                            <WhatsappIcon height="32" color="#D7282F" />
                        </a>
                    </div>

                    <Link to="/afonsodanielsouza/consultoria" className={styles.bannerLink}>
                        <img
                            src={bannerAsset}
                            alt="Consultoria fitness online — planos e valores"
                            className={styles.banner}
                        />
                    </Link>
                    <Link to="/afonsodanielsouza/antesdepois" className={styles.bannerLink}>
                        <img
                            src={bannerAsset2}
                            alt="Antes e depois — resultados de alunos"
                            className={styles.banner}
                        />
                    </Link>
                    <a href="https://loja.infinitepay.io/daniel-afonso-personal" target="_blank" rel="noopener noreferrer" className={styles.bannerLink}>
                        <img
                            src={bannerAsset3}
                            alt="Minha loja"
                            className={styles.banner}
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Afonsodanielsouza;
